import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronRight, Home } from 'lucide-react';
import { api } from './api';
import { formatDate } from './kit/time';
import { formatMoney } from './shared/money';

const when = (iso) => (iso ? formatDate(iso) : '—');

// Contact profile → Worksheets tab (ticket 158): the fee worksheets linked to this
// contact (ticket 155's `contact_id`). Same list endpoint and visibility rules as the
// Fee Worksheet page (an admin sees the tenant's, a loan officer their own). Worksheets
// saved before contact linking carry no `contact_id`, so they are not listed here.
// "Sent" = the worksheet has left draft (sent, viewed, approved or rejected).
export default function ContactWorksheets({ contactId }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setFailed(false);
    api.get('/fee-worksheet/list', { params: { contact_id: contactId } })
      .then(({ data }) => { if (alive) setRows(data?.worksheets || []); })
      .catch(() => { if (alive) { setRows([]); setFailed(true); } });
    return () => { alive = false; };
  }, [contactId, attempt]);

  if (rows === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-worksheets-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading worksheets...
      </div>
    );
  }
  if (failed) {
    return (
      <div role="alert" className="bg-red-50 border border-dashed border-red-200 rounded-2xl p-12 text-center text-red-700" data-testid="contact-worksheets-error">
        <p className="text-sm">Couldn't load worksheets.</p>
        <p className="text-xs mt-1">
          <button type="button" onClick={() => setAttempt((n) => n + 1)} className="underline font-semibold" data-testid="contact-worksheets-retry">Retry</button>
        </p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-worksheets-empty">
        <p className="text-sm">No worksheets are linked to this contact yet.</p>
        <p className="text-xs mt-1">Worksheets saved before contact linking are not linked.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-worksheets-list">
      {rows.map((w) => {
        const sent = !!w.borrower_status && w.borrower_status !== 'draft';
        const cash = w.worksheet?.totals?.estimated_cash_to_close;
        return (
          <Link
            key={w.worksheet_id}
            to={`/portal/fee-worksheet?worksheet=${encodeURIComponent(w.worksheet_id)}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
            data-testid={`contact-worksheet-${w.worksheet_id}`}
          >
            <Home size={14} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>
                {w.property_address || 'No property address'}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {when(w.created_at)}{w.borrower_name ? ` · ${w.borrower_name}` : ''}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              {cash != null && (
                <div className="text-sm font-bold" style={{ color: '#6BA82E' }} data-testid={`contact-worksheet-total-${w.worksheet_id}`}>
                  {formatMoney(cash)}
                </div>
              )}
              <div className="text-[11px] text-slate-400">cash to close</div>
            </div>
            <span
              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded flex-shrink-0 ${sent ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`contact-worksheet-sent-${w.worksheet_id}`}
            >
              {sent ? 'Sent' : 'Not sent'}
            </span>
            <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
          </Link>
        );
      })}
    </div>
  );
}
